/**
 * Bibliothèque d'objections — nourrie par les vrais appels.
 *
 * Chaque objection entendue pendant un appel copiloté entre ici en candidate,
 * avec la réponse que le commercial a réellement donnée et le résultat de
 * l'appel en regard. La revue hebdomadaire fait le tri : approuver ce qui a
 * fonctionné (le copilote s'en servira dès l'appel suivant), supprimer
 * l'inutile — le plan est explicite, on supprime plutôt qu'on reformule.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, Plus, RefreshCw, Trash2, X } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import Modal from "@/crm/components/Modal";

const EMPTY = { objection: "", response: "", status: "approved" };

const OUTCOME_LABELS = {
  MEETING: ["Rendez-vous", "bg-[#e6f5ec] text-[#1f7a48]"],
  INTERESTED: ["Intéressé", "bg-[#e6f5ec] text-[#1f7a48]"],
  CALLBACK: ["À rappeler", "bg-[#fdf3dd] text-[#8a6b1f]"],
  NOT_INTERESTED: ["Pas intéressé", "bg-[#fdeceb] text-[#d64a3a]"],
};

export default function ObjectionLibrary() {
  const [entries, setEntries] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/copilot/objections");
      setEntries(data.objections || []);
    } catch (e) {
      setEntries([]);
      toast.error(apiError(e.response?.data?.detail) || "Bibliothèque indisponible");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (entry) => {
    try {
      await api.put(`/copilot/objections/${entry.id}`, { status: "approved" });
      toast.success("Réponse approuvée — le copilote s'en servira dès le prochain appel");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (entry) => {
    try {
      await api.delete(`/copilot/objections/${entry.id}`);
      toast.success("Entrée supprimée");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!editing.objection.trim()) return toast.error("Décrivez l'objection");
    setSaving(true);
    try {
      if (editing.id) await api.put(`/copilot/objections/${editing.id}`, editing);
      else await api.post("/copilot/objections", editing);
      toast.success("Bibliothèque enregistrée");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (entries === null) {
    return (
      <p className="flex items-center justify-center gap-2 py-12 text-sm text-[#8a90a3]">
        <RefreshCw size={14} className="animate-spin" /> Chargement de la bibliothèque…
      </p>
    );
  }

  const candidates = entries.filter((e) => e.status === "candidate");
  const approved = entries.filter((e) => e.status === "approved");

  const Card = ({ entry, candidate }) => {
    const [outcomeLabel, outcomeClass] = OUTCOME_LABELS[entry.outcome] || [];
    return (
      <div data-testid="objection-entry"
        className={`rounded-2xl border bg-white p-3.5 ${candidate ? "border-[#f3d9a4]" : "border-[#e6e8ee]"}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[14px] font-medium text-[#16181d]">« {entry.objection} »</span>
              {candidate && (
                <span className="rounded-md bg-[#fdf3dd] px-1.5 py-0.5 font-mono text-[10.5px] uppercase text-[#8a6b1f]">
                  à trier
                </span>
              )}
              {outcomeLabel && (
                <span className={`rounded-md px-1.5 py-0.5 font-mono text-[10.5px] ${outcomeClass}`}
                  title="Résultat de l'appel où l'objection a été entendue">
                  {outcomeLabel}
                </span>
              )}
            </div>
            <p className="mt-1.5 text-[13px] leading-relaxed text-[#4c5164]">
              {entry.response
                ? <>Réponse donnée : <span className="text-[#16181d]">« {entry.response} »</span></>
                : <span className="text-[#9aa0b2]">Aucune réponse capturée — complétez avant d'approuver.</span>}
            </p>
            <div className="mt-1.5 flex flex-wrap gap-3 font-mono text-[11px] text-[#8a90a3]">
              <span>entendue {entry.heard_count || 1} fois</span>
              {entry.uses > 0 && <span>resservie {entry.uses} fois</span>}
              <span>{entry.source === "manual" ? "saisie manuelle" : "capturée sur appel"}</span>
            </div>
          </div>
          <div className="flex shrink-0 gap-1.5">
            {candidate && entry.response && (
              <button onClick={() => approve(entry)} title="Approuver — la réponse a fonctionné"
                className="rounded-lg border border-[#cfe9d9] p-1.5 text-[#2f9e5c] hover:bg-[#e6f5ec]">
                <Check size={14} />
              </button>
            )}
            <button onClick={() => setEditing({ ...entry })} title="Modifier"
              className="rounded-lg border border-[#e2e5ec] p-1.5 text-[#4c5164] hover:border-[#c9cedb]">
              <Pencil size={14} />
            </button>
            <button onClick={() => remove(entry)} title="Supprimer"
              className="rounded-lg border border-[#fbdcd8] p-1.5 text-[#d64a3a] hover:bg-[#fdeceb]">
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="objection-library">
      <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-[70ch] text-[13.5px] text-[#61667a] text-pretty">
          Chaque objection entendue en appel entre ici avec la réponse réellement donnée.
          Approuvez ce qui a fonctionné — le copilote s'en inspirera —, supprimez le reste.
        </p>
        <button data-testid="new-objection-btn" onClick={() => setEditing({ ...EMPTY })}
          className="flex items-center gap-2 rounded-[11px] bg-[#4a3ae0] px-[18px] py-[11px] text-sm font-medium text-white hover:bg-[#3a2cc8]">
          <Plus size={16} /> Ajouter une réponse
        </button>
      </div>

      {candidates.length > 0 && (
        <div className="mb-5">
          <h3 className="mb-2 font-mono text-[11px] uppercase tracking-[.12em] text-[#8a6b1f]">
            À trier en revue — {candidates.length}
          </h3>
          <div className="space-y-2.5">
            {candidates.map((entry) => <Card key={entry.id} entry={entry} candidate />)}
          </div>
        </div>
      )}

      <h3 className="mb-2 font-mono text-[11px] uppercase tracking-[.12em] text-[#8a90a3]">
        Approuvées — servies au copilote{approved.length ? ` (${approved.length})` : ""}
      </h3>
      {approved.length === 0 ? (
        <p className="rounded-2xl border border-[#e6e8ee] bg-white px-5 py-8 text-center text-[13px] text-[#8a90a3]">
          Rien d'approuvé pour l'instant — les objections des prochains appels arriveront ci-dessus.
        </p>
      ) : (
        <div className="space-y-2.5">
          {approved.map((entry) => <Card key={entry.id} entry={entry} />)}
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} className="max-w-[560px]" testid="objection-editor"
          label={editing.id ? "Modifier l'entrée" : "Nouvelle réponse"}>
          <div className="flex items-start justify-between">
            <h3 className="font-heading text-[17px] font-bold">
              {editing.id ? "Modifier l'entrée" : "Nouvelle réponse"}
            </h3>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-900">
              <X size={18} />
            </button>
          </div>

          <label className="mt-4 block text-[13px] font-medium text-[#4c5164]">
            L'objection, telle que le client la dit
            <input data-testid="objection-text" value={editing.objection}
              onChange={(e) => setEditing({ ...editing, objection: e.target.value })}
              placeholder="On a déjà quelqu'un pour ça"
              className="mt-1.5 w-full rounded-xl border border-[#e2e5ec] px-3 py-2.5 text-sm outline-none focus:border-[#5b4bff]" />
          </label>

          <label className="mt-3 block text-[13px] font-medium text-[#4c5164]">
            La réponse qui fonctionne — prononçable telle quelle
            <textarea data-testid="objection-response" value={editing.response} rows={3}
              onChange={(e) => setEditing({ ...editing, response: e.target.value })}
              placeholder="Justement : je ne vous propose pas de changer, seulement de comparer sur un point précis…"
              className="mt-1.5 w-full rounded-xl border border-[#e2e5ec] p-3 text-sm outline-none focus:border-[#5b4bff]" />
          </label>

          <div className="mt-5 flex gap-2">
            <button onClick={() => setEditing(null)}
              className="flex-1 rounded-xl border border-[#e2e5ec] py-2.5 text-sm text-[#4c5164] hover:border-[#c9cedb]">
              Annuler
            </button>
            <button data-testid="save-objection" onClick={save} disabled={saving}
              className="flex-1 rounded-xl bg-[#4a3ae0] py-2.5 text-sm font-medium text-white hover:bg-[#3a2cc8] disabled:opacity-50">
              Enregistrer
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
